import { pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import {
    CardPaymentMethod,
    PaymentProviderUsageMethod,
    SepaDirectDebitProviderState,
    SepaPaymentMethod,
} from '@mews-commander/data-access';
import { SelectFieldProps } from '@mews-commander/form';
import { Divider, DropdownItem, ListHeadline, Stack } from '@optimus-web/core';
import { FormSelectField, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { PaymentMethodImageType } from '@mews/business-components';
import { LocalizedTypography } from '@mews-commander/ui';
import { UNSPECIFIED_CREDIT_CARD_VALUE } from '@mews-commander/legacy-entities/src/ducks/payments';
import { NEW_CREDIT_CARD_VALUE } from '../../../../constants';
import PaymentCardIcon from '../../../../../../components/commander-ui/PaymentCardIcon';
import { PaymentMethodIcon } from '../../../../../../components/commander-ui/PaymentMethodIcon';
import { isCardPaymentMethod, isSepaPaymentMethod } from '../../../../utils';
import { ProcessPaymentFormField } from '../ProcessPaymentFormSchema';
import { hasPaymentMethodIdAllowedValue, sortByDate } from '../../../common/utils';
import { useGetPaymentMethodsForSelectSuspense } from '../../../common/queries';
import { useDefaultFormContext } from '../useDefaultFormValues';

interface PaymentMethodSelectFieldProps extends Omit<SelectFieldProps, 'options'> {
    accountId: string;
    isExternal?: boolean;
    optionalCreditCardPaymentDetails?: boolean;
    isPreauthorization?: boolean;
}

const PaymentMethodFormSelectIcon = ({ paymentMethod }: { paymentMethod?: CardPaymentMethod | SepaPaymentMethod }) => {
    if (paymentMethod && isCardPaymentMethod(paymentMethod)) {
        return <PaymentCardIcon type={paymentMethod.type} width={24} />;
    }

    if (paymentMethod && isSepaPaymentMethod(paymentMethod)) {
        return <PaymentMethodIcon type={PaymentMethodImageType.SepaDirectDebit} small />;
    }
    return null;
};

export const PaymentMethodSelectField = ({
    accountId,
    isExternal = true,
    optionalCreditCardPaymentDetails = false,
    isPreauthorization = false,
    name,
    ...other
}: PaymentMethodSelectFieldProps) => {
    const {
        readOnlyFormFields: { isPaymentMethodIdReadOnly },
    } = useDefaultFormContext();

    const {
        isSuccess,
        data: { paymentCards, sepas },
    } = useGetPaymentMethodsForSelectSuspense({
        accountId,
        onlyChargeable: true,
        onlyGateway: !isExternal,
    });

    const { setValue, getValues, watch } = useFormContext();
    const paymentPlanId = watch(ProcessPaymentFormField.PaymentPlanId);

    const enabledSepas = isExternal
        ? []
        : sepas.filter(
              (sepa) => sepa.state === SepaDirectDebitProviderState.Enabled && sepa.paymentPlanId === paymentPlanId
          );

    const availableCards = pipe(
        paymentCards ?? [],
        array.filter(
            (p) =>
                (!p.paymentPlanId || p.paymentPlanId === paymentPlanId) &&
                (isExternal || p.kind === PaymentProviderUsageMethod.Gateway)
        )
    );

    const availablePaymentMethods = [...availableCards, ...enabledSepas];

    const { [name]: selectedValue } = getValues();

    const hasAvailablePaymentMethods = availablePaymentMethods.length > 0;
    const defaultPaymentMethodId = hasAvailablePaymentMethods
        ? array.sortBy([sortByDate])(availablePaymentMethods)[0]?.id ?? ''
        : '';
    const allowUnspecifiedCardValue = optionalCreditCardPaymentDetails && isExternal && !isPreauthorization;
    const isSelectedValueAllowed = hasPaymentMethodIdAllowedValue(
        selectedValue,
        availablePaymentMethods,
        allowUnspecifiedCardValue
    );

    useEffect(() => {
        if (!isSelectedValueAllowed && !defaultPaymentMethodId && !hasAvailablePaymentMethods) {
            setValue(name, NEW_CREDIT_CARD_VALUE);
            return;
        }

        if (isSelectedValueAllowed || defaultPaymentMethodId === selectedValue) {
            return;
        }

        setValue(name, defaultPaymentMethodId);
    }, [name, selectedValue, setValue, defaultPaymentMethodId, hasAvailablePaymentMethods, isSelectedValueAllowed]);

    if (!isSuccess) {
        return null;
    }

    return (
        <FormSelectField
            name={name}
            data-test-select-name={name}
            label={lt('ProcessPaymentMewsPaymentsPaymentMethodSelectLabel')}
            helper={lt('ProcessPaymentMewsPaymentsPaymentMethodSelectHelper')}
            readOnly={isPaymentMethodIdReadOnly}
            displayMapper={(i: React.ReactElement) => (
                <Stack spacing="100">
                    <PaymentMethodFormSelectIcon
                        paymentMethod={availablePaymentMethods.find(({ id }) => id === i.props.value)}
                    />
                    {i.props.label}
                </Stack>
            )}
            {...other}
        >
            <ListHeadline>
                <LocalizedTypography
                    textStyle="bodyMedium"
                    variant="tertiary"
                    textKey={TextKey.ProcessPaymentMewsPaymentsPaymentMethodSelectHeadlineCards}
                    as="div"
                />
            </ListHeadline>
            <DropdownItem
                label={lt('CreditCardNew')}
                value={NEW_CREDIT_CARD_VALUE}
                data-test-select-item-value={NEW_CREDIT_CARD_VALUE}
                key={NEW_CREDIT_CARD_VALUE}
            />
            {availableCards.map(({ title, id }) => (
                <DropdownItem label={title} value={id} data-test-select-item-value={id} key={id} />
            ))}
            {allowUnspecifiedCardValue ? (
                <DropdownItem
                    label={lt('Unspecified')}
                    value={UNSPECIFIED_CREDIT_CARD_VALUE}
                    data-test-select-item-value={UNSPECIFIED_CREDIT_CARD_VALUE}
                    key={UNSPECIFIED_CREDIT_CARD_VALUE}
                />
            ) : null}
            {enabledSepas.length > 0 && (
                <>
                    <Divider />
                    <ListHeadline>
                        <LocalizedTypography
                            textStyle="bodyMedium"
                            variant="tertiary"
                            textKey={
                                TextKey.ProcessPaymentMewsPaymentsPaymentMethodSelectHeadlineAlternativePaymentMethods
                            }
                            as="div"
                        />
                    </ListHeadline>
                </>
            )}
            {enabledSepas.map(({ title, id }) => (
                <DropdownItem label={title} value={id} data-test-select-item-value={id} key={id} />
            ))}
        </FormSelectField>
    );
};
